/**
 * COP (Coefficient of Performance) Meter Component
 * Displays COP value with color-coded performance indicator
 * @module COPMeter
 */

import React, { memo, useMemo } from "react";
import PropTypes from "prop-types";
import { Tooltip } from "antd";

// COP performance thresholds
const COP_THRESHOLDS = {
  EXCELLENT: 4.0,
  GOOD: 3.0,
};

// Performance colors
const COP_COLORS = {
  EXCELLENT: "green",
  GOOD: "#FF5F1F",
  POOR: "red",
};

/**
 * Determine COP indicator color based on performance
 * @param {number} cop - Coefficient of Performance value
 * @returns {string} Color code for the COP value
 */
const getCOPColor = (cop) => {
  if (cop >= COP_THRESHOLDS.EXCELLENT) return COP_COLORS.EXCELLENT;
  if (cop >= COP_THRESHOLDS.GOOD) return COP_COLORS.GOOD;
  return COP_COLORS.POOR;
};

/**
 * COP Meter SVG Text Component
 * @param {Object} props - Component props
 * @param {number} props.cop - Coefficient of Performance value
 * @param {number} [props.x=0] - X coordinate
 * @param {number} [props.y=0] - Y coordinate
 * @param {number} [props.fontSize=10] - Font size
 * @returns {JSX.Element} SVG text element with color-coded COP value
 */
export const COPMeter = memo(({ cop, x, y, fontSize }) => {
  const color = useMemo(() => getCOPColor(cop), [cop]);
  const displayValue = useMemo(() => cop.toFixed(1), [cop]);

  return (
    <g>
      <Tooltip title="Coefficient of Performance" color="white">
        <rect 
          x={x} 
          y={y - (fontSize || 10)} 
          width={80} 
          height={fontSize || 10 * 1.5} 
          fill="transparent" 
          style={{ cursor: 'pointer' }}
        />
      </Tooltip>
      <text x={x} y={y} fill={color} fontSize={fontSize} style={{ pointerEvents: 'none' }}>
        COP {displayValue}
      </text>
    </g>
  );
});

COPMeter.displayName = "COPMeter";

COPMeter.propTypes = {
  cop: PropTypes.number.isRequired,
  x: PropTypes.number,
  y: PropTypes.number,
  fontSize: PropTypes.number,
};

COPMeter.defaultProps = {
  x: 0,
  y: 0,
  fontSize: 10,
};